import { createContext, useContext, useRef, useState, type ReactNode } from "react";
import { getPrivateSessionState } from "@/lib/api/private-session";
type Attempt = { token: object; target: string; generation: number; pending: boolean; error: string | null };
type AttemptState = { attempt: Attempt | null; claim: (target: string, manual: boolean) => object | null; own: (token: object) => void; finish: (token: object, error: string | null) => void };
const StoreBindingAttemptContext = createContext<AttemptState | null>(null);
/** Lives above generation remounts, but is keyed by the actually inspected actor/store binding. */
export function StoreBindingAttemptProvider({ children }: { children: ReactNode }) {
  const current = useRef<Attempt | null>(null);
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const write = (next: Attempt) => { current.current = next; setAttempt(next); };
  const claim = (target: string, manual: boolean) => {
    const previous = current.current;
    if ((!manual && previous?.target === target) || (previous?.pending && previous.generation === getPrivateSessionState().generation)) return null;
    const token = {}; write({ token, target, generation: getPrivateSessionState().generation, pending: true, error: null }); return token;
  };
  const own = (token: object) => { if (current.current?.token === token) write({ ...current.current, generation: getPrivateSessionState().generation }); };
  const finish = (token: object, error: string | null) => {
    const record = current.current; const session = getPrivateSessionState();
    if (record?.token !== token || record.generation !== session.generation || session.changing) return;
    write({ ...record, pending: false, error });
  };
  return <StoreBindingAttemptContext.Provider value={{ attempt, claim, own, finish }}>{children}</StoreBindingAttemptContext.Provider>;
}
export function useStoreBindingAttempt() {
  const value = useContext(StoreBindingAttemptContext);
  if (!value) throw new Error("Store binding requires its identity owner");
  return value;
}
